"use client";

/**
 * Product Inquiry Dialog
 *
 * Dialog for customers to submit inquiries about custom-priced products.
 * Used on both the brand shop (FashionShopProductCard) and the full
 * product page (ProductDisplay).
 *
 * Calls api.orders.customOrders.createInquiry on the backend.
 */

import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";
import { X, Send, Loader2, CheckCircle2, MessageCircle } from "lucide-react";
import Image from "next/image";
import { toast } from "@/lib/toast";

interface ProductInquiryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  productName: string;
  productImage?: string;
  brandName: string;
  brandId: string;
  productId: string;
}

export default function ProductInquiryDialog({
  isOpen,
  onClose,
  productName,
  productImage,
  brandName,
  brandId,
  productId,
}: ProductInquiryDialogProps) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const createInquiry = useMutation(api.orders.customOrders.createInquiry);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !phone.trim() || !message.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);

    try {
      await createInquiry({
        brandId: brandId as Id<"brands">,
        customerName: name.trim(),
        customerPhone: phone.trim(),
        productId: productId as Id<"products">,
        productName,
        productImage,
        message: message.trim(),
      });

      setIsSuccess(true);
      toast.success("Inquiry submitted successfully!");

      // Reset after a moment
      setTimeout(() => {
        setName("");
        setPhone("");
        setMessage("");
        setIsSuccess(false);
        onClose();
      }, 2000);
    } catch (error) {
      console.error("Failed to submit inquiry:", error);
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setName("");
      setPhone("");
      setMessage("");
      setIsSuccess(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Dialog */}
      <div className="relative bg-white w-full max-w-md shadow-2xl border border-gray-100 animate-in fade-in zoom-in-95 duration-200">
        {/* Close button */}
        <button
          onClick={handleClose}
          disabled={isSubmitting}
          className="absolute top-4 right-4 p-1.5 hover:bg-gray-100 rounded-full transition-colors z-10"
        >
          <X className="h-4 w-4 text-gray-500" />
        </button>

        {/* Success State */}
        {isSuccess ? (
          <div className="p-8 text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-green-50 rounded-full flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">Inquiry Sent!</h3>
            <p className="text-sm text-gray-500">
              {brandName} will get back to you with pricing details shortly.
            </p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="p-6 pb-0">
              <div className="flex items-center gap-2 mb-4">
                <MessageCircle className="h-5 w-5 text-amber-600" />
                <h3 className="text-lg font-bold text-gray-900">
                  Enquire About This Product
                </h3>
              </div>

              {/* Product Preview */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-5">
                {productImage && (
                  <div className="relative w-14 h-14 flex-shrink-0 bg-white rounded overflow-hidden border border-gray-100">
                    <Image
                      src={productImage}
                      alt={productName}
                      fill
                      className="object-cover"
                      unoptimized
                    />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {productName}
                  </p>
                  <p className="text-xs text-gray-500">
                    by {brandName} • Custom Price
                  </p>
                </div>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 pt-0 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                  Your Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your full name"
                  required
                  disabled={isSubmitting}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-400 transition-colors disabled:opacity-50"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+256 7XX XXX XXX"
                  required
                  disabled={isSubmitting}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-400 transition-colors disabled:opacity-50"
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                  Your Message
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Describe what you're looking for, preferred size, color, quantity, etc."
                  rows={3}
                  required
                  disabled={isSubmitting}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-400 transition-colors disabled:opacity-50"
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting || !name.trim() || !phone.trim() || !message.trim()}
                className="w-full h-11 bg-black text-white text-xs font-semibold uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Send Inquiry
                  </>
                )}
              </button>

              <p className="text-[10px] text-center text-gray-400">
                {brandName} will contact you via phone with pricing details.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
